import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, School } from "@/lib/api";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Building2,
  Users,
  GraduationCap,
  BookOpen,
  BarChart3,
  Plus,
  Copy,
  RefreshCw,
  Search,
  Upload,
  Eye,
  EyeOff,
  KeyRound,
  Trash2,
  ImagePlus,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";
import { toast } from "sonner";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  type ColumnDef,
  type SortingState,
  type RowSelectionState,
} from "@tanstack/react-table";
import { EditableCell } from "@/components/school/EditableCell";
import { EditableSelectCell } from "@/components/school/EditableSelectCell";
import { DataTable } from "@/components/school/DataTable";
import { BulkAssignToolbar } from "@/components/school/BulkAssignToolbar";

const USER_ID_REGEX = /^[a-zA-Z0-9._-]{3,30}$/;

function validateUserId(value: string): string | null {
  if (!value) return "User ID is required";
  if (value.length < 3) return "User ID must be at least 3 characters";
  if (value.length > 30) return "User ID must be at most 30 characters";
  if (/\s/.test(value)) return "User ID cannot contain spaces";
  if (!USER_ID_REGEX.test(value)) return "User ID can only contain letters, numbers, dots, underscores, and hyphens";
  return null;
}

function validatePassword(value: string): string | null {
  if (!value) return "Password is required";
  if (value.length < 6) return "Password must be at least 6 characters";
  return null;
}

export default function SchoolAdminDashboard() {
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(tab || "overview");
  const [school, setSchool] = useState<School | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (tab) setActiveTab(tab);
  }, [tab]);

  useEffect(() => {
    if (user?.school_id) {
      loadSchool();
    }
  }, [user?.school_id]);

  const loadSchool = async () => {
    if (!user?.school_id) return;
    try {
      const data = await api.getSchoolDetails(user.school_id);
      setSchool(data);
    } catch (err: any) {
      toast.error(err?.message || "Failed to load school");
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    navigate(value === "overview" ? "/school" : `/school/${value}`, { replace: true });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-lg text-gray-500">Loading school dashboard...</div>
      </div>
    );
  }

  if (!school) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700">No school assigned</h2>
          <p className="text-gray-500 mt-2">Contact Ithopu admin to set up your school.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          {school.logo_url ? (
            <img src={school.logo_url} alt={school.name} className="w-12 h-12 rounded-lg object-contain" />
          ) : (
            <div className="w-12 h-12 bg-cyan-100 text-cyan-600 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6" />
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{school.name}</h1>
            <p className="text-sm text-gray-500">{school.slug}.ithopu.com</p>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="grid w-full grid-cols-6 mb-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="teachers">Teachers</TabsTrigger>
          <TabsTrigger value="students">Students</TabsTrigger>
          <TabsTrigger value="classrooms">Classrooms</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab school={school} />
        </TabsContent>
        <TabsContent value="teachers">
          <TeachersTab school={school} onRefresh={loadSchool} />
        </TabsContent>
        <TabsContent value="students">
          <StudentsTab school={school} onRefresh={loadSchool} />
        </TabsContent>
        <TabsContent value="classrooms">
          <ClassroomsTab school={school} onRefresh={loadSchool} />
        </TabsContent>
        <TabsContent value="analytics">
          <AnalyticsTab school={school} />
        </TabsContent>
        <TabsContent value="settings">
          <SettingsTab school={school} onRefresh={loadSchool} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ===== OVERVIEW TAB =====
function OverviewTab({ school }: { school: School }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard icon={<GraduationCap className="w-5 h-5" />} label="Teachers" value={school.counts?.teachers || 0} color="blue" />
        <StatCard icon={<Users className="w-5 h-5" />} label="Students" value={school.counts?.students || 0} color="green" />
        <StatCard icon={<BookOpen className="w-5 h-5" />} label="Classrooms" value={school.counts?.classrooms || 0} color="purple" />
        <StatCard icon={<BarChart3 className="w-5 h-5" />} label="Active" value="-" color="orange" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">School Code</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <code className="text-2xl font-mono font-bold tracking-wider bg-gray-100 dark:bg-gray-800 px-4 py-2 rounded">
              {school.school_code}
            </code>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText(school.school_code);
                toast.success("School code copied!");
              }}
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-sm text-gray-500 mt-2">Share this code with teachers to join your school</p>
        </CardContent>
      </Card>

      {school.description && (
        <Card>
          <CardHeader><CardTitle className="text-lg">About</CardTitle></CardHeader>
          <CardContent><p className="text-gray-600 dark:text-gray-400">{school.description}</p></CardContent>
        </Card>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, color }: { icon: React.ReactNode; label: string; value: number | string; color: string }) {
  const colorClasses: Record<string, string> = {
    blue: "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
    green: "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400",
    purple: "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400",
    orange: "bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400",
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
            {icon}
          </div>
          <div>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-sm text-gray-500">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ===== TEACHERS TAB =====
function TeachersTab({ school, onRefresh }: { school: School; onRefresh: () => void }) {
  const [teachers, setTeachers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUserId, setNewUserId] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");
  const [creating, setCreating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetTarget, setResetTarget] = useState<{ id: string; username: string } | null>(null);
  const [resetPassword, setResetPassword] = useState("");
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkCsv, setBulkCsv] = useState("");
  const [bulkUploading, setBulkUploading] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);

  useEffect(() => { loadTeachers(); }, [school.id]);

  const loadTeachers = async () => {
    try {
      const data = await api.getSchoolTeachers(school.id);
      setTeachers(data.teachers);
    } catch (err: any) {
      toast.error(err?.message || "Failed to load teachers");
    } finally {
      setLoading(false);
    }
  };

  const userIdError = validateUserId(newUserId);
  const passwordError = validatePassword(newPassword);

  const handleCreateTeacher = async () => {
    if (userIdError || passwordError) { toast.error(userIdError || passwordError || "Fix validation errors"); return; }
    setCreating(true);
    try {
      await api.createSchoolTeacher({
        school_id: school.id,
        user_id_str: newUserId,
        password: newPassword,
        first_name: newFirstName || undefined,
        last_name: newLastName || undefined,
      });
      toast.success(`Teacher "${newUserId}" created!`);
      setNewUserId(""); setNewPassword(""); setNewFirstName(""); setNewLastName("");
      setShowCreateForm(false);
      loadTeachers();
      onRefresh();
    } catch (err: any) {
      toast.error(err?.message || "Failed to create teacher");
    } finally {
      setCreating(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetTarget || !resetPassword) return;
    try {
      await api.resetSchoolStudentPassword(school.id, resetTarget.id, resetPassword);
      toast.success(`Password reset for ${resetTarget.username}`);
      setResetTarget(null);
      setResetPassword("");
    } catch (err: any) {
      toast.error(err?.message || "Failed to reset password");
    }
  };

  const handleBulkImport = async () => {
    if (!bulkCsv.trim()) return;
    setBulkUploading(true);
    try {
      const lines = bulkCsv.trim().split("\n").filter((l) => l.trim());
      const teacherRows = lines.map((line) => {
        const parts = line.split(",").map((p) => p.trim());
        return {
          user_id_str: parts[0],
          password: parts[1] || "changeme123",
          first_name: parts[2] || undefined,
          last_name: parts[3] || undefined,
        };
      });
      const result = await api.bulkCreateSchoolTeachers({ school_id: school.id, teachers: teacherRows });
      toast.success(`Created ${result.successful} of ${result.total} teachers`);
      if (result.failed > 0) {
        const failures = result.results.filter((r: any) => r.status === "failed");
        failures.forEach((f: any) => toast.error(`${f.user_id_str}: ${f.error}`));
      }
      setBulkCsv("");
      setShowBulkImport(false);
      loadTeachers();
      onRefresh();
    } catch (err: any) {
      toast.error(err?.message || "Bulk import failed");
    } finally {
      setBulkUploading(false);
    }
  };

  const handleToggleStatus = async (userId: string, currentStatus: string) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    try {
      await api.toggleSchoolMemberStatus({ school_id: school.id, user_id: userId, status: newStatus });
      toast.success(`Teacher ${newStatus === "active" ? "activated" : "deactivated"}`);
      loadTeachers();
      onRefresh();
    } catch (err: any) {
      toast.error(err?.message || "Failed to update status");
    }
  };

  const handleInlineSave = async (rowId: string, field: string, value: string) => {
    try {
      await api.updateSchoolStudent({
        school_id: school.id,
        student_id: rowId,
        [field]: value,
      });
      setTeachers((prev) => prev.map((t) => t.user_id === rowId ? { ...t, [field]: value } : t));
      toast.success("Updated");
    } catch (err: any) {
      toast.error(err?.message || "Failed to update");
      throw err;
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const columns = useMemo<ColumnDef<any>[]>(() => [
    {
      accessorKey: "username",
      header: "Username",
      cell: ({ row }) => <span className="font-medium">@{row.original.username}</span>,
      enableSorting: true,
    },
    {
      accessorKey: "first_name",
      header: "First Name",
      cell: ({ row }) => (
        <EditableCell value={row.original.first_name || ""} rowId={row.original.user_id} columnId="first_name" onSave={handleInlineSave} />
      ),
      enableSorting: true,
    },
    {
      accessorKey: "last_name",
      header: "Last Name",
      cell: ({ row }) => (
        <EditableCell value={row.original.last_name || ""} rowId={row.original.user_id} columnId="last_name" onSave={handleInlineSave} />
      ),
      enableSorting: true,
    },
    {
      accessorKey: "classroom_count",
      header: "Classrooms",
      enableSorting: true,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
          row.original.status === "active"
            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
            : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
        }`}>
          {row.original.status === "active" ? "Active" : "Inactive"}
        </span>
      ),
      enableSorting: true,
    },
    {
      id: "created_at",
      accessorFn: (row) => row.joined_at || row.created_at,
      header: "Created",
      cell: ({ getValue }) => <span className="text-gray-500">{formatDate(getValue() as string | null)}</span>,
      enableSorting: true,
    },
    {
      accessorKey: "updated_at",
      header: "Last Updated",
      cell: ({ getValue }) => <span className="text-gray-500">{formatDate(getValue() as string | null)}</span>,
      enableSorting: true,
    },
    {
      id: "actions",
      header: () => <span className="flex justify-end">Actions</span>,
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => setResetTarget({ id: row.original.user_id, username: row.original.username })} title="Reset password">
            <KeyRound className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleToggleStatus(row.original.user_id, row.original.status)}
            title={row.original.status === "active" ? "Deactivate teacher" : "Activate teacher"}
          >
            {row.original.status === "active" ? (
              <ToggleRight className="w-4 h-4 text-green-600" />
            ) : (
              <ToggleLeft className="w-4 h-4 text-gray-400" />
            )}
          </Button>
        </div>
      ),
      enableSorting: false,
    },
  ], [school.id]);

  const table = useReactTable({
    data: teachers,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row.user_id,
  });

  return (
    <div className="space-y-6">
      {/* Actions bar */}
      <div className="flex flex-wrap gap-3">
        <Button onClick={() => setShowCreateForm(!showCreateForm)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Teacher
        </Button>
        <Button variant="outline" onClick={() => setShowBulkImport(!showBulkImport)}>
          <Upload className="w-4 h-4 mr-2" />
          Bulk Import
        </Button>
      </div>

      {/* Create teacher form */}
      {showCreateForm && (
        <Card>
          <CardHeader><CardTitle className="text-lg">Create Teacher Account</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>User ID *</Label>
                <Input placeholder="e.g., mr.smith" value={newUserId} onChange={(e) => setNewUserId(e.target.value)} autoComplete="off" className={newUserId && userIdError ? "border-red-400" : ""} />
                {newUserId && userIdError ? (
                  <p className="text-xs text-red-500">{userIdError}</p>
                ) : (
                  <p className="text-xs text-gray-500">Letters, numbers, dots, underscores, hyphens. Login at {school.slug}.ithopu.com</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Password *</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Min 6 characters"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    className={newPassword && passwordError ? "border-red-400" : ""}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {newPassword && passwordError && <p className="text-xs text-red-500">{passwordError}</p>}
              </div>
              <div className="space-y-2">
                <Label>First Name</Label>
                <Input placeholder="Optional" value={newFirstName} onChange={(e) => setNewFirstName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Last Name</Label>
                <Input placeholder="Optional" value={newLastName} onChange={(e) => setNewLastName(e.target.value)} />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={handleCreateTeacher} disabled={creating || !!(newUserId && userIdError) || !!(newPassword && passwordError)}>{creating ? "Creating..." : "Create Teacher"}</Button>
              <Button variant="outline" onClick={() => setShowCreateForm(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bulk import */}
      {showBulkImport && (
        <Card>
          <CardHeader><CardTitle className="text-lg">Bulk Import Teachers (CSV)</CardTitle></CardHeader>
          <CardContent>
            <textarea
              className="w-full h-32 p-3 border rounded-lg font-mono text-sm"
              placeholder={"user_id,password,first_name,last_name\nmr.smith,pass123,John,Smith\nms.jones,pass456,Jane,Jones"}
              value={bulkCsv}
              onChange={(e) => setBulkCsv(e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-1">Format: user_id,password,first_name,last_name (one per line)</p>
            <div className="flex gap-2 mt-3">
              <Button onClick={handleBulkImport} disabled={bulkUploading}>{bulkUploading ? "Importing..." : "Import"}</Button>
              <Button variant="outline" onClick={() => setShowBulkImport(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reset password modal */}
      {resetTarget && (
        <Card className="border-yellow-300">
          <CardHeader><CardTitle className="text-lg">Reset Password for {resetTarget.username}</CardTitle></CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input type="text" placeholder="New password (min 6 chars)" value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} />
              <Button onClick={handleResetPassword}>Reset</Button>
              <Button variant="outline" onClick={() => { setResetTarget(null); setResetPassword(""); }}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Teacher table */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Teachers ({teachers.length})</CardTitle></CardHeader>
        <CardContent>
          <DataTable
            table={table}
            isLoading={loading}
            emptyMessage='No teachers yet. Click "Create Teacher" to add one.'
          />
        </CardContent>
      </Card>
    </div>
  );
}

// ===== STUDENTS TAB =====
function StudentsTab({ school, onRefresh }: { school: School; onRefresh: () => void }) {
  const [students, setStudents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUserId, setNewUserId] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");
  const [newGrade, setNewGrade] = useState("");
  const [creating, setCreating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetTarget, setResetTarget] = useState<{ id: string; username: string } | null>(null);
  const [resetPassword, setResetPassword] = useState("");
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkCsv, setBulkCsv] = useState("");
  const [bulkUploading, setBulkUploading] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [classrooms, setClassrooms] = useState<any[]>([]);

  useEffect(() => { loadStudents(); }, [school.id, page, search]);
  useEffect(() => {
    api.getSchoolClassrooms(school.id).then((data) => setClassrooms(data.classrooms)).catch(() => {});
  }, [school.id]);

  const loadStudents = async () => {
    try {
      const data = await api.getSchoolStudents(school.id, { page, limit: 50, search: search || undefined });
      setStudents(data.students);
      setTotal(data.total);
    } catch (err: any) {
      toast.error(err?.message || "Failed to load students");
    } finally {
      setLoading(false);
    }
  };

  const studentUserIdError = validateUserId(newUserId);
  const studentPasswordError = validatePassword(newPassword);

  const handleCreateStudent = async () => {
    if (studentUserIdError || studentPasswordError) { toast.error(studentUserIdError || studentPasswordError || "Fix validation errors"); return; }
    setCreating(true);
    try {
      await api.createSchoolStudent({
        school_id: school.id,
        user_id_str: newUserId,
        password: newPassword,
        first_name: newFirstName || undefined,
        last_name: newLastName || undefined,
        grade: newGrade || undefined,
      });
      toast.success(`Student "${newUserId}" created!`);
      setNewUserId(""); setNewPassword(""); setNewFirstName(""); setNewLastName(""); setNewGrade("");
      setShowCreateForm(false);
      loadStudents();
      onRefresh();
    } catch (err: any) {
      toast.error(err?.message || "Failed to create student");
    } finally {
      setCreating(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetTarget || !resetPassword) return;
    try {
      await api.resetSchoolStudentPassword(school.id, resetTarget.id, resetPassword);
      toast.success(`Password reset for ${resetTarget.username}`);
      setResetTarget(null);
      setResetPassword("");
    } catch (err: any) {
      toast.error(err?.message || "Failed to reset password");
    }
  };

  const handleBulkImport = async () => {
    if (!bulkCsv.trim()) return;
    setBulkUploading(true);
    try {
      const lines = bulkCsv.trim().split("\n").filter((l) => l.trim());
      const studentRows = lines.map((line) => {
        const parts = line.split(",").map((p) => p.trim());
        return {
          user_id_str: parts[0],
          password: parts[1] || "changeme123",
          first_name: parts[2] || undefined,
          last_name: parts[3] || undefined,
          grade: parts[4] || undefined,
        };
      });
      const result = await api.bulkCreateSchoolStudents({ school_id: school.id, students: studentRows });
      toast.success(`Created ${result.successful} of ${result.total} students`);
      if (result.failed > 0) {
        const failures = result.results.filter((r: any) => r.status === "failed");
        failures.forEach((f: any) => toast.error(`${f.user_id_str}: ${f.error}`));
      }
      setBulkCsv("");
      setShowBulkImport(false);
      loadStudents();
      onRefresh();
    } catch (err: any) {
      toast.error(err?.message || "Bulk import failed");
    } finally {
      setBulkUploading(false);
    }
  };

  const handleToggleStatus = async (userId: string, currentStatus: string) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    try {
      await api.toggleSchoolMemberStatus({ school_id: school.id, user_id: userId, status: newStatus });
      toast.success(`Student ${newStatus === "active" ? "activated" : "deactivated"}`);
      loadStudents();
      onRefresh();
    } catch (err: any) {
      toast.error(err?.message || "Failed to update status");
    }
  };

  const handleInlineSave = async (rowId: string, field: string, value: string) => {
    try {
      await api.updateSchoolStudent({
        school_id: school.id,
        student_id: rowId,
        [field]: value,
      });
      setStudents((prev) => prev.map((s) => s.user_id === rowId ? { ...s, [field]: value } : s));
      toast.success("Updated");
    } catch (err: any) {
      toast.error(err?.message || "Failed to update");
      throw err;
    }
  };

  const handleBulkAssign = async (classroomId: string) => {
    const selectedIds = Object.keys(rowSelection);
    if (selectedIds.length === 0) return;
    try {
      await api.assignStudentsToClassroom({
        school_id: school.id,
        classroom_id: classroomId,
        add_student_ids: selectedIds,
      });
      const classroom = classrooms.find((c) => c.id === classroomId);
      toast.success(`${selectedIds.length} student(s) assigned to ${classroom?.name || "classroom"}`);
      setRowSelection({});
    } catch (err: any) {
      toast.error(err?.message || "Failed to assign students");
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const columns = useMemo<ColumnDef<any>[]>(() => [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && "indeterminate")}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      size: 40,
    },
    {
      accessorKey: "username",
      header: "Username",
      cell: ({ row }) => <span className="font-medium">@{row.original.username}</span>,
      enableSorting: true,
    },
    {
      accessorKey: "first_name",
      header: "First Name",
      cell: ({ row }) => (
        <EditableCell value={row.original.first_name || ""} rowId={row.original.user_id} columnId="first_name" onSave={handleInlineSave} />
      ),
      enableSorting: true,
    },
    {
      accessorKey: "last_name",
      header: "Last Name",
      cell: ({ row }) => (
        <EditableCell value={row.original.last_name || ""} rowId={row.original.user_id} columnId="last_name" onSave={handleInlineSave} />
      ),
      enableSorting: true,
    },
    {
      accessorKey: "grade",
      header: "Grade",
      cell: ({ row }) => (
        <EditableCell value={row.original.grade || ""} rowId={row.original.user_id} columnId="grade" onSave={handleInlineSave} />
      ),
      enableSorting: true,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
          row.original.status === "active"
            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
            : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
        }`}>
          {row.original.status === "active" ? "Active" : "Inactive"}
        </span>
      ),
      enableSorting: true,
    },
    {
      id: "created_at",
      accessorFn: (row) => row.joined_at || row.created_at,
      header: "Created",
      cell: ({ getValue }) => <span className="text-gray-500">{formatDate(getValue() as string | null)}</span>,
      enableSorting: true,
    },
    {
      accessorKey: "updated_at",
      header: "Last Updated",
      cell: ({ getValue }) => <span className="text-gray-500">{formatDate(getValue() as string | null)}</span>,
      enableSorting: true,
    },
    {
      id: "actions",
      header: () => <span className="flex justify-end">Actions</span>,
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => setResetTarget({ id: row.original.user_id, username: row.original.username })} title="Reset password">
            <KeyRound className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleToggleStatus(row.original.user_id, row.original.status)}
            title={row.original.status === "active" ? "Deactivate student" : "Activate student"}
          >
            {row.original.status === "active" ? (
              <ToggleRight className="w-4 h-4 text-green-600" />
            ) : (
              <ToggleLeft className="w-4 h-4 text-gray-400" />
            )}
          </Button>
        </div>
      ),
      enableSorting: false,
    },
  ], [school.id]);

  const table = useReactTable({
    data: students,
    columns,
    state: { sorting, rowSelection },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row.user_id,
    enableRowSelection: true,
  });

  const selectedCount = Object.keys(rowSelection).length;

  return (
    <div className="space-y-6">
      {/* Actions bar */}
      <div className="flex flex-wrap gap-3">
        <Button onClick={() => setShowCreateForm(!showCreateForm)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Student
        </Button>
        <Button variant="outline" onClick={() => setShowBulkImport(!showBulkImport)}>
          <Upload className="w-4 h-4 mr-2" />
          Bulk Import
        </Button>
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              className="pl-10"
              placeholder="Search students..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
        </div>
      </div>

      {/* Create student form */}
      {showCreateForm && (
        <Card>
          <CardHeader><CardTitle className="text-lg">Create Student Account</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>User ID *</Label>
                <Input placeholder="e.g., john.doe" value={newUserId} onChange={(e) => setNewUserId(e.target.value)} autoComplete="off" className={newUserId && studentUserIdError ? "border-red-400" : ""} />
                {newUserId && studentUserIdError ? (
                  <p className="text-xs text-red-500">{studentUserIdError}</p>
                ) : (
                  <p className="text-xs text-gray-500">Letters, numbers, dots, underscores, hyphens. Login at {school.slug}.ithopu.com</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Password *</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Min 6 characters"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    className={newPassword && studentPasswordError ? "border-red-400" : ""}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {newPassword && studentPasswordError && <p className="text-xs text-red-500">{studentPasswordError}</p>}
              </div>
              <div className="space-y-2">
                <Label>Grade</Label>
                <Input placeholder="e.g., 5, 10, K" value={newGrade} onChange={(e) => setNewGrade(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>First Name</Label>
                <Input placeholder="Optional" value={newFirstName} onChange={(e) => setNewFirstName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Last Name</Label>
                <Input placeholder="Optional" value={newLastName} onChange={(e) => setNewLastName(e.target.value)} />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={handleCreateStudent} disabled={creating || !!(newUserId && studentUserIdError) || !!(newPassword && studentPasswordError)}>{creating ? "Creating..." : "Create Student"}</Button>
              <Button variant="outline" onClick={() => setShowCreateForm(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bulk import */}
      {showBulkImport && (
        <Card>
          <CardHeader><CardTitle className="text-lg">Bulk Import Students (CSV)</CardTitle></CardHeader>
          <CardContent>
            <textarea
              className="w-full h-32 p-3 border rounded-lg font-mono text-sm"
              placeholder={"user_id,password,first_name,last_name,grade\njohn.doe,pass123,John,Doe,5\njane.smith,pass456,Jane,Smith,6"}
              value={bulkCsv}
              onChange={(e) => setBulkCsv(e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-1">Format: user_id,password,first_name,last_name,grade (one per line)</p>
            <div className="flex gap-2 mt-3">
              <Button onClick={handleBulkImport} disabled={bulkUploading}>{bulkUploading ? "Importing..." : "Import"}</Button>
              <Button variant="outline" onClick={() => setShowBulkImport(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reset password modal */}
      {resetTarget && (
        <Card className="border-yellow-300">
          <CardHeader><CardTitle className="text-lg">Reset Password for {resetTarget.username}</CardTitle></CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input type="text" placeholder="New password (min 6 chars)" value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} />
              <Button onClick={handleResetPassword}>Reset</Button>
              <Button variant="outline" onClick={() => { setResetTarget(null); setResetPassword(""); }}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Student table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Students ({total})</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            table={table}
            isLoading={loading}
            emptyMessage="No students found"
            toolbar={
              <BulkAssignToolbar
                selectedCount={selectedCount}
                classrooms={classrooms.map((c) => ({ id: c.id, name: c.name }))}
                onAssign={handleBulkAssign}
                onClearSelection={() => setRowSelection({})}
              />
            }
          />
          {total > 50 && (
            <div className="flex justify-center gap-2 mt-4">
              <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</Button>
              <span className="text-sm text-gray-500 self-center">Page {page} of {Math.ceil(total / 50)}</span>
              <Button variant="outline" size="sm" disabled={page * 50 >= total} onClick={() => setPage(page + 1)}>Next</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ===== CLASSROOMS TAB =====
function ClassroomsTab({ school, onRefresh }: { school: School; onRefresh: () => void }) {
  const [classrooms, setClassrooms] = useState<any[]>([]);
  const [teachers, setTeachers] = useState<any[]>([]);
  const [allStudents, setAllStudents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [newTeacherId, setNewTeacherId] = useState("");
  const [newGradeLevel, setNewGradeLevel] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newMaxStudents, setNewMaxStudents] = useState(30);
  const [creating, setCreating] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);

  const [manageTarget, setManageTarget] = useState<any | null>(null);
  const [classroomStudents, setClassroomStudents] = useState<string[]>([]);
  const [originalClassroomStudents, setOriginalClassroomStudents] = useState<string[]>([]);
  const [manageSaving, setManageSaving] = useState(false);

  useEffect(() => {
    loadClassrooms();
    loadTeachers();
  }, [school.id]);

  const loadClassrooms = async () => {
    try {
      const data = await api.getSchoolClassrooms(school.id);
      setClassrooms(data.classrooms);
    } catch (err: any) {
      toast.error(err?.message || "Failed to load classrooms");
    } finally {
      setLoading(false);
    }
  };

  const loadTeachers = async () => {
    try {
      const data = await api.getSchoolTeachers(school.id);
      setTeachers(data.teachers);
    } catch (err: any) {
      // silent fail - teachers list is supplementary
    }
  };

  const loadAllStudents = async () => {
    try {
      const data = await api.getSchoolStudents(school.id, { limit: 500 });
      setAllStudents(data.students);
    } catch (err: any) {
      toast.error(err?.message || "Failed to load students");
    }
  };

  const handleCreate = async () => {
    if (!newName || !newTeacherId) { toast.error("Name and teacher are required"); return; }
    setCreating(true);
    try {
      await api.createSchoolClassroom({
        school_id: school.id,
        name: newName,
        teacher_id: newTeacherId,
        grade_level: newGradeLevel || undefined,
        description: newDescription || undefined,
        max_students: newMaxStudents,
      });
      toast.success("Classroom created!");
      setNewName(""); setNewTeacherId(""); setNewGradeLevel(""); setNewDescription(""); setNewMaxStudents(30);
      setShowCreateForm(false);
      loadClassrooms();
      onRefresh();
    } catch (err: any) {
      toast.error(err?.message || "Failed to create classroom");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (classroomId: string) => {
    if (!confirm("Delete this classroom? This action cannot be undone.")) return;
    try {
      await api.deleteSchoolClassroom(school.id, classroomId);
      toast.success("Classroom deleted");
      loadClassrooms();
      onRefresh();
    } catch (err: any) {
      toast.error(err?.message || "Failed to delete classroom");
    }
  };

  const handleInlineSave = async (rowId: string, field: string, value: string) => {
    try {
      const updateData: any = { school_id: school.id, classroom_id: rowId };
      if (field === "max_students") {
        updateData[field] = Number(value);
      } else {
        updateData[field] = value;
      }
      await api.updateSchoolClassroom(updateData);
      setClassrooms((prev) => prev.map((c) => {
        if (c.id !== rowId) return c;
        if (field === "teacher_id") {
          const teacher = teachers.find((t) => t.user_id === value);
          return { ...c, teacher_id: value, teacher: teacher ? { first_name: teacher.first_name, last_name: teacher.last_name, username: teacher.username } : c.teacher };
        }
        if (field === "max_students") return { ...c, [field]: Number(value) };
        return { ...c, [field]: value };
      }));
      toast.success("Updated");
    } catch (err: any) {
      toast.error(err?.message || "Failed to update");
      throw err;
    }
  };

  const openManageStudents = async (c: any) => {
    setManageTarget(c);
    try {
      const students = await api.getClassroomStudents(c.id);
      const currentIds = students.map((s: any) => s.student_id || s.id);
      setClassroomStudents(currentIds);
      setOriginalClassroomStudents(currentIds);
    } catch {
      setClassroomStudents([]);
      setOriginalClassroomStudents([]);
    }
    await loadAllStudents();
  };

  const toggleStudent = (studentId: string) => {
    setClassroomStudents((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSaveStudents = async () => {
    if (!manageTarget) return;
    setManageSaving(true);
    try {
      const toAdd = classroomStudents.filter((id) => !originalClassroomStudents.includes(id));
      const toRemove = originalClassroomStudents.filter((id) => !classroomStudents.includes(id));

      if (toAdd.length === 0 && toRemove.length === 0) {
        toast.info("No changes made");
        setManageTarget(null);
        return;
      }

      await api.assignStudentsToClassroom({
        school_id: school.id,
        classroom_id: manageTarget.id,
        add_student_ids: toAdd.length > 0 ? toAdd : undefined,
        remove_student_ids: toRemove.length > 0 ? toRemove : undefined,
      });
      toast.success("Students updated!");
      setManageTarget(null);
      loadClassrooms();
    } catch (err: any) {
      toast.error(err?.message || "Failed to update students");
    } finally {
      setManageSaving(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const getTeacherDisplayName = (c: any) => {
    if (c.teacher?.first_name || c.teacher?.last_name) {
      return `${c.teacher.first_name || ""} ${c.teacher.last_name || ""}`.trim();
    }
    return c.teacher?.username || "Unknown";
  };

  const teacherOptions = useMemo(() =>
    teachers.map((t) => ({
      value: t.user_id,
      label: t.first_name || t.last_name ? `${t.first_name || ""} ${t.last_name || ""}`.trim() : t.username,
    })),
    [teachers]
  );

  const columns = useMemo<ColumnDef<any>[]>(() => [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => (
        <EditableCell value={row.original.name || ""} rowId={row.original.id} columnId="name" onSave={handleInlineSave} />
      ),
      enableSorting: true,
    },
    {
      accessorKey: "teacher_id",
      header: "Teacher",
      cell: ({ row }) => (
        <EditableSelectCell
          value={row.original.teacher_id || ""}
          displayValue={getTeacherDisplayName(row.original)}
          rowId={row.original.id}
          columnId="teacher_id"
          options={teacherOptions}
          onSave={handleInlineSave}
        />
      ),
      enableSorting: true,
      sortingFn: (a, b) => {
        const aName = getTeacherDisplayName(a.original);
        const bName = getTeacherDisplayName(b.original);
        return aName.localeCompare(bName);
      },
    },
    {
      accessorKey: "grade_level",
      header: "Grade",
      cell: ({ row }) => (
        <EditableCell value={row.original.grade_level || ""} rowId={row.original.id} columnId="grade_level" onSave={handleInlineSave} />
      ),
      enableSorting: true,
    },
    {
      accessorKey: "student_count",
      header: "Students",
      enableSorting: true,
    },
    {
      accessorKey: "class_code",
      header: "Class Code",
      cell: ({ row }) => (
        <code className="text-xs font-mono bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">{row.original.class_code}</code>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "max_students",
      header: "Max Students",
      cell: ({ row }) => (
        <EditableCell value={String(row.original.max_students || 30)} rowId={row.original.id} columnId="max_students" onSave={handleInlineSave} />
      ),
      enableSorting: true,
    },
    {
      accessorKey: "created_at",
      header: "Created",
      cell: ({ getValue }) => <span className="text-gray-500">{formatDate(getValue() as string | null)}</span>,
      enableSorting: true,
    },
    {
      accessorKey: "updated_at",
      header: "Last Updated",
      cell: ({ getValue }) => <span className="text-gray-500">{formatDate(getValue() as string | null)}</span>,
      enableSorting: true,
    },
    {
      id: "actions",
      header: () => <span className="flex justify-end">Actions</span>,
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => openManageStudents(row.original)} title="Manage students">
            <Users className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleDelete(row.original.id)} title="Delete">
            <Trash2 className="w-4 h-4 text-red-500" />
          </Button>
        </div>
      ),
      enableSorting: false,
    },
  ], [school.id, teachers, teacherOptions]);

  const table = useReactTable({
    data: classrooms,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row.id,
  });

  return (
    <div className="space-y-6">
      <div className="flex gap-3">
        <Button onClick={() => setShowCreateForm(!showCreateForm)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Classroom
        </Button>
      </div>

      {/* Create classroom form */}
      {showCreateForm && (
        <Card>
          <CardHeader><CardTitle className="text-lg">Create Classroom</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Classroom Name *</Label>
                <Input placeholder="e.g., Grade 5 - Section A" value={newName} onChange={(e) => setNewName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Teacher *</Label>
                <select className="w-full border rounded-lg px-3 py-2 text-sm" value={newTeacherId} onChange={(e) => setNewTeacherId(e.target.value)}>
                  <option value="">Select a teacher...</option>
                  {teachers.map((t) => (
                    <option key={t.user_id} value={t.user_id}>
                      {t.first_name || t.last_name ? `${t.first_name || ""} ${t.last_name || ""}`.trim() : t.username}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Grade Level</Label>
                <Input placeholder="e.g., 5, 10, K" value={newGradeLevel} onChange={(e) => setNewGradeLevel(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Max Students</Label>
                <Input type="number" value={newMaxStudents} onChange={(e) => setNewMaxStudents(Number(e.target.value))} />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Description</Label>
                <Input placeholder="Optional description" value={newDescription} onChange={(e) => setNewDescription(e.target.value)} />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={handleCreate} disabled={creating}>{creating ? "Creating..." : "Create"}</Button>
              <Button variant="outline" onClick={() => setShowCreateForm(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Manage students modal */}
      {manageTarget && (
        <Card className="border-green-300">
          <CardHeader>
            <CardTitle className="text-lg">Manage Students: {manageTarget.name}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-500 mb-4">Check/uncheck students to add or remove them from this classroom.</p>
            {allStudents.length === 0 ? (
              <p className="text-gray-500">No students in school yet</p>
            ) : (
              <div className="max-h-64 overflow-y-auto space-y-1">
                {allStudents.filter((s: any) => s.status === "active").map((s: any) => (
                  <label key={s.user_id} className="flex items-center gap-3 p-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={classroomStudents.includes(s.user_id)}
                      onChange={() => toggleStudent(s.user_id)}
                      className="w-4 h-4"
                    />
                    <span className="font-medium text-sm">
                      {s.first_name || s.last_name ? `${s.first_name || ""} ${s.last_name || ""}`.trim() : s.username}
                    </span>
                    <span className="text-xs text-gray-500">@{s.username}{s.grade ? ` | Grade ${s.grade}` : ""}</span>
                  </label>
                ))}
              </div>
            )}
            <div className="flex gap-2 mt-4">
              <Button onClick={handleSaveStudents} disabled={manageSaving}>{manageSaving ? "Saving..." : "Save Changes"}</Button>
              <Button variant="outline" onClick={() => setManageTarget(null)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Classrooms table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Classrooms ({classrooms.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            table={table}
            isLoading={loading}
            emptyMessage='No classrooms yet. Click "Create Classroom" to get started.'
          />
        </CardContent>
      </Card>
    </div>
  );
}

// ===== ANALYTICS TAB =====
function AnalyticsTab({ school }: { school: School }) {
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(30);

  useEffect(() => { loadAnalytics(); }, [school.id, dateRange]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const data = await api.getSchoolAnalytics(school.id, dateRange);
      setAnalytics(data);
    } catch (err: any) {
      toast.error(err?.message || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <p className="text-gray-500">Loading analytics...</p>;
  if (!analytics) return <p className="text-gray-500">No analytics available</p>;

  const summary = analytics.summary || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Label>Date Range:</Label>
        <select
          className="border rounded px-3 py-1.5 text-sm"
          value={dateRange}
          onChange={(e) => setDateRange(Number(e.target.value))}
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard icon={<Users className="w-5 h-5" />} label="Active Students" value={summary.active_students || 0} color="green" />
        <StatCard icon={<BarChart3 className="w-5 h-5" />} label="Avg Accuracy" value={`${summary.avg_accuracy || 0}%`} color="blue" />
        <StatCard icon={<BookOpen className="w-5 h-5" />} label="Problems Solved" value={summary.total_problems_solved || 0} color="purple" />
        <StatCard icon={<BarChart3 className="w-5 h-5" />} label="Total Sessions" value={summary.total_sessions || 0} color="orange" />
      </div>

      {analytics.classroom_breakdown?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-lg">Per-Classroom Breakdown</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Classroom</th>
                    <th className="text-left p-2">Teacher</th>
                    <th className="text-left p-2">Grade</th>
                    <th className="text-right p-2">Students</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.classroom_breakdown.map((c: any) => (
                    <tr key={c.id} className="border-b">
                      <td className="p-2 font-medium">{c.name}</td>
                      <td className="p-2 text-gray-500">{c.teacher}</td>
                      <td className="p-2 text-gray-500">{c.grade_level || "-"}</td>
                      <td className="p-2 text-right">{c.student_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {analytics.top_students?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-lg">Top Students</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {analytics.top_students.map((s: any, i: number) => (
                <div key={s.user_id} className="flex items-center justify-between p-2">
                  <span className="font-medium">#{i + 1} {s.student?.first_name || s.student?.username || "Unknown"}</span>
                  <span className="text-sm text-gray-500">{s.problems_solved} problems | {s.accuracy}% accuracy</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ===== SETTINGS TAB =====
function SettingsTab({ school, onRefresh }: { school: School; onRefresh: () => void }) {
  const [name, setName] = useState(school.name);
  const [description, setDescription] = useState(school.description || "");
  const [address, setAddress] = useState(school.address || "");
  const [city, setCity] = useState(school.city || "");
  const [state, setState] = useState(school.state || "");
  const [contactEmail, setContactEmail] = useState(school.contact_email || "");
  const [contactPhone, setContactPhone] = useState(school.contact_phone || "");
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateSchool({
        school_id: school.id,
        name, description, address, city, state,
        contact_email: contactEmail,
        contact_phone: contactPhone,
      } as any);
      toast.success("School updated!");
      onRefresh();
    } catch (err: any) {
      toast.error(err?.message || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerateCode = async () => {
    if (!confirm("Regenerate school code? The old code will stop working.")) return;
    setRegenerating(true);
    try {
      await api.regenerateSchoolCode(school.id);
      toast.success("School code regenerated!");
      onRefresh();
    } catch (err: any) {
      toast.error(err?.message || "Failed to regenerate code");
    } finally {
      setRegenerating(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image must be less than 2MB");
      return;
    }

    setUploading(true);
    try {
      const ext = file.name.split(".").pop() || "png";
      const filePath = `${school.id}/logo.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from("school-logos")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("school-logos")
        .getPublicUrl(filePath);

      const logoUrl = urlData.publicUrl;

      await api.updateSchool({
        school_id: school.id,
        logo_url: logoUrl,
      } as any);

      toast.success("Logo uploaded!");
      onRefresh();
    } catch (err: any) {
      toast.error(err?.message || "Failed to upload logo");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-6">
      {/* Logo upload */}
      <Card>
        <CardHeader><CardTitle className="text-lg">School Logo</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            {school.logo_url ? (
              <img src={school.logo_url} alt="School logo" className="w-24 h-24 rounded-lg object-contain border" />
            ) : (
              <div className="w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center border border-dashed">
                <ImagePlus className="w-8 h-8 text-gray-400" />
              </div>
            )}
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleLogoUpload}
              />
              <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? "Uploading..." : school.logo_url ? "Change Logo" : "Upload Logo"}
              </Button>
              <p className="text-xs text-gray-500 mt-2">Max 2MB. PNG, JPG, or SVG.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">School Information</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>School Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Subdomain</Label>
              <Input value={`${school.slug}.ithopu.com`} disabled className="bg-gray-100" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Description</Label>
              <textarea className="w-full border rounded-lg p-3 text-sm" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input value={address} onChange={(e) => setAddress(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>City</Label>
              <Input value={city} onChange={(e) => setCity(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>State</Label>
              <Input value={state} onChange={(e) => setState(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Contact Email</Label>
              <Input value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Contact Phone</Label>
              <Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
            </div>
          </div>
          <Button className="mt-4" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save Changes"}</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">School Code</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <code className="text-xl font-mono font-bold bg-gray-100 dark:bg-gray-800 px-4 py-2 rounded">{school.school_code}</code>
            <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(school.school_code); toast.success("Copied!"); }}>
              <Copy className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={handleRegenerateCode} disabled={regenerating}>
              <RefreshCw className="w-4 h-4 mr-1" />
              {regenerating ? "..." : "Regenerate"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
